import Layer from './Layer';
import { toDate } from '../utils';
import moment from 'moment';


export const FORECASTS = {
    5: '氾濫発生情報',
    4: '氾濫危険情報',
    3: '氾濫警戒情報',
    2: '氾濫注意情報'
};
export const RISKS = {
    4: '災害切迫',
    3: '危険',
    2: '警戒',
    1: '注意',
    0: '今後の情報等に留意'
}

// キキクル 洪水害
export default class Flood extends Layer {
    _latest = process.env.REACT_APP_FLOOD_URL;

    _layout = {
        'flood-risk-name': {
            "symbol-placement": "line",
            "symbol-spacing": 400,
            "text-field": "{name}",
            "text-max-angle": 45,
            "text-letter-spacing": 0.1,
            "text-size": 12,
            "text-rotation-alignment": "map"
        },
        'flood-forecast-name': {
            "symbol-placement": "line",
            "symbol-spacing": 500,
            "text-field": "{name}",
            "text-max-angle": 45,
            "text-letter-spacing": 0.1,
            "text-size": 14,
            "text-rotation-alignment": "map"
        },
        'flood-risk-line-halo': {
            "line-join": "miter",
            "line-miter-limit": 2   // mapbox のデフォルト値
        },
        'flood-forecast-line-halo': {
            "line-join": "miter",
            "line-miter-limit": 2   // mapbox のデフォルト値
        },
    };

    constructor() {
        const id = 'flood';

        const layers = {
            'flood-risk-name': {
                type: "symbol",
                source: id,
                "source-layer": "flood",
                minzoom: 10,
                filter: ["==", "TYPE", "1"]
            },
            'flood-forecast-name': {
                type: "symbol",
                source: id,
                "source-layer": "flood",
                minzoom: 7,
                filter: ["==", "TYPE", "2"]
            },
            'flood-risk-line-halo': {
                type: "line",
                source: id,
                "source-layer": "flood",
                filter: ["all", ["==", "TYPE", "1"], ["==", "FLOODRISK", "4"]]
            },
            'flood-risk-line': {
                type: "line",
                source: id,
                "source-layer": "flood",
                filter: ["==", "TYPE", "1"]
            },
            'flood-forecast-line-halo': {
                type: "line",
                source: id,
                "source-layer": "flood",
                filter: ["all", ["==", "TYPE", "2"], ["==", "FLOODFCST", "5"]]
            },
            'flood-forecast-line': {
                type: "line",
                source: id,
                "source-layer": "flood",
                filter: ["==", "TYPE", "2"]
            }
        };

        const paintOptions = {
            "flood-risk-name": {
                "text-color": "rgba(200, 200, 200, 1)",
                "text-halo-color": "#000",
                "text-halo-width": 1,
                "text-opacity": 1
            },
            "flood-forecast-name": {
                "text-color": "rgba(200, 200, 200, 1)",
                "text-halo-color": "#000",
                "text-halo-width": 1,
                "text-opacity": 1
            },
            'flood-risk-line': {
                "line-color": {
                    "property": "FLOODRISK",
                    type: "categorical",
                    stops: [
                        ["0", "#3DFFFF"],
                        ["1", "#FAF500"],
                        ["2", "#FA4500"],
                        ["3", "#6C008C"],
                        ["4", "#000000"]
                    ]
                },
                "line-width": ['interpolate',
                    ['linear'],
                    ['zoom'],
                    2, 0.4,
                    6, 0.8,
                    12, 2.4,
                    15, 12,
                ]
            },
            'flood-risk-line-halo': {
                "line-color": "rgba(255, 255, 255, 0.7)",
                "line-width": 0.5,
                "line-gap-width": ['interpolate',
                    ['linear'],
                    ['zoom'],
                    2, 0.4,
                    6, 0.8,
                    12, 2.4,
                    15, 12,
                ],
            },
            "flood-forecast-line": {
                "line-color": {
                    "property": "FLOODFCST",
                    type: "categorical",
                    stops: [
                        ["0", "rgb(128, 255, 255)"],
                        ["1", "rgb(128, 255, 255)"],
                        ["2", "rgb(250, 245, 0)"],
                        ["3", "rgb(255, 90, 0)"],
                        ["4", "rgb(230, 11,108)"],
                        ["5", "rgb(0, 0, 0)"]
                    ]
                },
                "line-width": ['interpolate',
                    ['linear'],
                    ['zoom'],
                    2, 2,
                    6, 4,
                    12, 8,
                    15, 40,
                ]
            },
            'flood-forecast-line-halo': {
                "line-color": "rgba(255, 255, 255, 0.7)",
                "line-width": 1.0,
                "line-gap-width": ['interpolate',
                    ['linear'],
                    ['zoom'],
                    2, 2,
                    6, 4,
                    12, 8,
                    15, 40,
                ]
            },
        };

        // レイヤー要素。この単位でレイヤリングする。配列順に最前面→再後面
        const elements = [
            { id: 'flood-risk-name', layers: ['flood-risk-name'], isShow: true },           // 洪水害の危険度
            { id: 'flood-forecast-name', layers: ['flood-forecast-name'], isShow: true },    // 指定河川洪水予報
            { id: 'flood-forecast', layers: ['flood-forecast-line', 'flood-forecast-line-halo'], isShow: true },    // 指定河川洪水予報
            { id: 'flood-risk', layers: ['flood-risk-line', 'flood-risk-line-halo'], isShow: true },           // 洪水害の危険度
        ];

        super(id, layers, paintOptions, elements);
    }

    set latest(latest) { this._latest = latest; }
    get latest() { return this._latest; }

    onClick(e) {
        const layers = ['flood-risk-line', 'flood-forecast-line'];
        const features = this.map.queryRenderedFeatures(e.point, {
            layers: layers,
        });

        if (!features.length) {
            return;
        }

        const content = features.map(x => {
            const forecast = (x.properties.FLOODFCST) ? FORECASTS[x.properties.FLOODFCST] : '';
            const risk = (x.properties.FLOODRISK) ? RISKS[x.properties.FLOODRISK] : '';
            return (x.properties.name ? x.properties.name : '（名称不明）') + '：' + forecast + risk;
        });

        return '洪水害 危険度分布<ul>' + content.map(x => `<li>${x}</li>`).join('') + '</ul>';
    }

    setDemoUrl(datetime) {
        const YYYYMMDDHHmm = moment(datetime).utc().format("YYYYMMDDHHmm");
        this._latest = `${process.env.REACT_APP_FLOOD_DEMO_URL}/${YYYYMMDDHHmm}/tile.json`;
    };

    async add() {
        return fetch(this._latest)
            .then(res => res.json())
            .then(json => {
                this.map.addSource(this.id, {
                    type: 'vector',
                    tiles: json.tiles,
                    minzoom: 0,
                    maxzoom: 11,
                });

                this._elements
                    .forEach(element => {
                        element.layers.forEach(id => {
                            this.map.addLayer({
                                id: id
                                , ...this.layers[id]
                                , layout: { ...this._layout[id], visibility: (element.isShow ? 'visible' : 'none') }
                                , paint: { ...this._paintOptions[id] }
                            });
                        });
                    });


                this.datetime = toDate(json.ref_time);
                console.log(`loaded Flood ${this.datetime}`)
            });
    }

    remove() {
        Object.keys(this.layers)
            .map(x => this.map.removeLayer(x));

        this.map.removeSource(this.id);
    }

    showElement(element, isShow) {
        this._elements
            .filter(x => (x.id === element))
            .forEach(x => {
                x.layers.forEach(id =>
                    this.map.setLayoutProperty(id, 'visibility', isShow ? 'visible' : 'none')
                );
                x.isShow = isShow;
            });
    };

    getRivers() {
        if (!this.map) {
            return { risks: [], forecasts: [] };
        }

        const features = this.map.querySourceFeatures('flood', { sourceLayer: 'flood' });

        const risks = {};
        const forecasts = {};
        features.forEach(feature => {
            const properties = feature.properties;
            switch (properties.TYPE) {
                case '1':
                    {
                        const key = properties.RIVERCODE;
                        if (risks[key] == null) {
                            risks[key] = { name: properties.name, code: properties.FLOODRISK };
                        } else {
                            risks[key].code = (risks[key].code < properties.FLOODRISK)
                                ? properties.FLOODRISK
                                : risks[key].code;
                        }
                    }
                    break;

                case '2':
                    {
                        const key = properties.FAREACODE;
                        if (forecasts[key] == null) {
                            forecasts[key] = { name: properties.name, code: properties.FLOODFCST };
                        } else {
                            forecasts[key].code = (forecasts[key].code < properties.FLOODFCST)
                                ? properties.FLOODFCST
                                : forecasts[key].code;
                        }
                    }
                    break;

                default:
                    throw new Error('invalid flood type.');
            }
        })

        return { risks: risks, forecasts: forecasts };
    }

    // ヒットした河川の中央を返す。
    getRiskRiverPosition(code, risk) {
        const features = this.map.querySourceFeatures('flood', {
            sourceLayer: 'flood',
            filter: ["all", ["==", "RIVERCODE", code], ["==", "FLOODRISK", risk]]
        });

        if (features.length === 0) {
            return;
        }

        return this.getMedian(features);
    }

    // ヒットした河川の中央を返す。
    getForecastRiverPosition(code, forecast) {
        const features = this.map.querySourceFeatures('flood', {
            sourceLayer: 'flood',
            filter: ["all", ["==", "FAREACODE", code], ["==", "FLOODFCST", forecast]]
        });

        if (features.length === 0) {
            return;
        }

        return this.getMedian(features);
    }

    getRiskRiverAABB(code) {
        const features = this.map.querySourceFeatures('flood', { sourceLayer: 'flood', filter: ["==", "RIVERCODE", code] });
        if (features.length === 0) {
            return;
        }

        // 外接する矩形を求める。
        return this.getAABB(features);
    }

    getForecastRiverAABB(code) {
        const features = this.map.querySourceFeatures('flood', { sourceLayer: 'flood', filter: ["==", "FAREACODE", code] });
        if (features.length === 0) {
            return;
        }

        // 外接する矩形を求める。
        return this.getAABB(features);
    }

    // 外接する矩形を求める。
    getAABB(features) {
        const AABB = { min: { lat: null, lon: null }, max: { lat: null, lon: null } }

        features.forEach(feature => {
            const geometry = feature.geometry;
            switch (geometry.type) {
                case 'LineString':
                    geometry.coordinates.forEach(coordinate => {
                        AABB.min.lon = (AABB.min.lon == null) ? coordinate[0] : Math.min(AABB.min.lon, coordinate[0]);
                        AABB.min.lat = (AABB.min.lat == null) ? coordinate[1] : Math.min(AABB.min.lat, coordinate[1]);
                        AABB.max.lon = (AABB.max.lon == null) ? coordinate[0] : Math.max(AABB.max.lon, coordinate[0]);
                        AABB.max.lat = (AABB.max.lat == null) ? coordinate[1] : Math.max(AABB.max.lat, coordinate[1]);
                    });
                    break;

                case 'MultiLineString':
                    geometry.coordinates.forEach(coordinates => {
                        coordinates.forEach(coordinate => {
                            AABB.min.lon = (AABB.min.lon == null) ? coordinate[0] : Math.min(AABB.min.lon, coordinate[0]);
                            AABB.min.lat = (AABB.min.lat == null) ? coordinate[1] : Math.min(AABB.min.lat, coordinate[1]);
                            AABB.max.lon = (AABB.max.lon == null) ? coordinate[0] : Math.max(AABB.max.lon, coordinate[0]);
                            AABB.max.lat = (AABB.max.lat == null) ? coordinate[1] : Math.max(AABB.max.lat, coordinate[1]);
                        });
                    });
                    break;

                default:
                    throw new Error('invalid geometry type.');
            }
        });

        return AABB;
    }

    // 中央を返す。
    getMedian(features) {
        const coordinates = [];
        features.forEach(feature => {
            const geometry = feature.geometry;
            switch (geometry.type) {
                case 'LineString':
                    coordinates.push(...geometry.coordinates);
                    break;

                case 'MultiLineString':
                    geometry.coordinates.forEach(x => {
                        coordinates.push(...x);
                    });
                    break;

                default:
                    throw new Error('invalid geometry type.');
            }
        });

        if (coordinates.length === 0) {
            return;
        }

        return coordinates[Math.floor(coordinates.length / 2)];
    }


};


